import {
    computeStatus,
    isAnswered,
    Status,
} from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/services";
import { type QuestionnaireOverviewData } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/types";
import { StatusTagCircular } from "@design-system/DataDisplay/StatusTagCircular";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Box } from "@design-system/Layout/Box";
import { type ColumnDef } from "@design-system/Table";
import { AnswerStatus, QuestionStatusFilter } from "@generated/client/graphql";
import { useMemo } from "react";
import { useColumnsTranslations } from "./i18n";

export const statusColumn: ColumnDef<QuestionnaireOverviewData[number]> = {
    id: "status",
    accessorFn: ({ answer }) => {
        const status = answer?.status ?? AnswerStatus.Unknown;
        const value = answer?.value;

        return computeStatus({ status, value });
    },
    sortingFn: (rowA, rowB) => {
        const rowAStatus = rowA.original.answer?.status;
        const rowBStatus = rowB.original.answer?.status;
        const rowAScore =
            rowAStatus === AnswerStatus.Validated
                ? 1
                : rowAStatus === AnswerStatus.Answered
                  ? 0
                  : -1;
        const rowBScore =
            rowBStatus === AnswerStatus.Validated
                ? 1
                : rowBStatus === AnswerStatus.Answered
                  ? 0
                  : -1;

        return rowAScore - rowBScore;
    },
    header: () => "",
    cell: ({ row }) => {
        const { status = AnswerStatus.Unknown, value } =
            row.original?.answer ?? {};

        const icon = useMemo(
            () => computeStatus({ status, value }),
            [status, value],
        );

        const { t, keys } = useColumnsTranslations();

        const textTooltip = useMemo(() => {
            if (icon === Status.Validated) {
                return t(keys.tooltip_status_validated);
            }
            if (icon === Status.InProgress) {
                return t(keys.tooltip_status_in_progress);
            }
            return t(keys.tooltip_status_not_started);
        }, [icon]);

        return (
            <Box className="place-content-center" display="grid">
                <Tooltip>
                    <Tooltip.Trigger>
                        <StatusTagCircular status={icon} size="md" />
                    </Tooltip.Trigger>
                    <Tooltip.Content>{textTooltip}</Tooltip.Content>
                </Tooltip>
            </Box>
        );
    },
    meta: {
        cellClassName: "min-w-[50px] max-w-[50px]",
        headerClassName: "min-w-[50px] max-w-[50px]",
        colWidth: "50px",
    },
    filterFn: (row, columnId, filterValue) => {
        if (
            columnId !== "status" ||
            (Array.isArray(filterValue) && filterValue.length === 0)
        ) {
            return true;
        }

        const status = row.original.answer?.status ?? AnswerStatus.Unknown;
        const value = row.original.answer?.value;

        const computedStatus =
            status === AnswerStatus.Validated
                ? QuestionStatusFilter.Validated
                : isAnswered(value)
                  ? QuestionStatusFilter.InProgress
                  : QuestionStatusFilter.NotStarted;

        return filterValue.includes(computedStatus);
    },
};
